/**
 * O1 — MatchOverlay: composes the metadata strip + side stat panels
 * into a single DOM overlay rooted at a positioning container.
 *
 * Lives as a sibling to `<ReplayScene>` (the 3D canvas), not inside
 * it. Consumer wraps both in a `position: relative` parent and mounts
 * `<MatchOverlay>` on top:
 *
 *   <div style={{position: "relative", inset: 0}}>
 *     <ReplayScene battle={b} tick={t} ... />
 *     <MatchOverlay battle={b} tick={t} ... />
 *   </div>
 *
 * `panels` toggles individual widgets — supports the Twitch overlay
 * route (commercial/01-frontend-integration.md §8 line 214) reading
 * `?panels=metadata,statA` query params and forwarding here.
 *
 * Theming: pass `tokens` to override token defaults per-instance, OR
 * set the same `--pp-*` CSS variables on an ancestor. The overlay
 * root applies token vars in `style` so consumers can layer their own
 * stylesheet at a higher specificity if needed.
 */

import type { CSSProperties, ReactNode } from "react";
import type { MechState, ParsedBattle } from "@pilot-protocol/replay-parser";
import { frameAt } from "../replay/adapter";
import {
  MatchMetadataStrip,
  type MatchScore,
} from "./MatchMetadataStrip";
import { ReasoningPanel } from "./ReasoningPanel";
import { SideStatPanel } from "./SideStatPanel";
import { TelemetryFeed } from "./TelemetryFeed";
import { telemetryRows } from "./telemetry";
import { AccoladeFlash } from "./AccoladeFlash";
import { detectAccolades, visibleAccoladeAt } from "./narrative";
import type { TranscriptEntry } from "./transcript";
import { eventStream } from "../replay/events";
import {
  DEFAULT_TOKENS,
  resolveTokens,
  tokensToCssVars,
  type OverlayTokens,
} from "./tokens";

export type OverlayPanel =
  | "metadata"
  | "statA"
  | "statB"
  | "reasoningA"
  | "reasoningB"
  | "telemetry"
  | "accolades";

const DEFAULT_PANELS: ReadonlyArray<OverlayPanel> = ["metadata", "statA", "statB"];

export interface MatchOverlayProps {
  battle: ParsedBattle;
  /** Current playback tick. */
  tick: number;
  /** Which panels to mount. Omitting a panel removes it entirely. */
  panels?: ReadonlyArray<OverlayPanel>;
  /** Per-instance token overrides. */
  tokens?: Partial<OverlayTokens>;
  /** Best-of-N match score (optional). */
  score?: MatchScore;
  /** Optional Glicko ratings for either side. */
  ratingA?: number;
  ratingB?: number;
  /** Optional archetype hints per side. */
  archetypeA?: string;
  archetypeB?: string;
  /** Show numeric legend under the side zone-armor diagrams. */
  showZoneLegend?: boolean;
  /**
   * Background mode — `"normal"` paints the subtle retro graph grid,
   * `"chromakey"` paints solid green (`#00ff00`) for OBS chromakey
   * removal, `"transparent"` is fully see-through (for OBS browser
   * source over arbitrary scenes).
   */
  background?: "normal" | "chromakey" | "transparent";
  /**
   * Cosmetic content for side A's stat panel — drop in a voxel
   * portrait, faction emblem, chassis thumbnail, or any other React
   * node. The slot reserves `--pp-cosmetic-slot-h` height; pass
   * `null` to skip entirely (the panel tightens up).
   *
   * Canonical fill: `<CosmeticPortrait voxJsonPath={...} />`. Consumer
   * is responsible for resolving `Agent.cosmetics.{chassis_skin_id,
   * engineer_avatar_id (pending VCQ-2), ...}` to a `vox_json` path
   * via the cosmetic catalog before passing it down. Keeping the slot
   * as `ReactNode` rather than a typed cosmetic ID decouples the
   * overlay package from `@pilot-protocol/api-types` and lets the
   * Twitch / OBS overlay variant ship pre-resolved <img> thumbnails
   * for perf without dragging in the cosmetic loader.
   */
  cosmeticA?: ReactNode;
  /** Cosmetic content for side B's stat panel. */
  cosmeticB?: ReactNode;
  /**
   * Loadout slots — render under the zone armor diagram inside each
   * side panel, above the status badges. Consumer supplies a small
   * weapons/tools list. Collapses entirely when omitted.
   */
  loadoutA?: ReactNode;
  loadoutB?: ReactNode;
  /**
   * O2 — agent transcript per side. Used by the `reasoningA` /
   * `reasoningB` panels to surface the current-turn Decide-phase
   * reasoning_text + tool calls. Consumer parses the gzipped JSONL
   * upstream (see `docs/dev/launch-wiring/02-transcript-transport.md
   * §8` for the SDK hooks) and passes the parsed entry list here.
   * Omit (or pass `[]`) when transcripts aren't loaded — the panel
   * renders an explicit "transcript unavailable" placeholder.
   */
  transcriptA?: ReadonlyArray<TranscriptEntry>;
  transcriptB?: ReadonlyArray<TranscriptEntry>;
  /** Show technical reasoning stats (model / tokens / latency). */
  showReasoningStats?: boolean;
  /** O3 — max telemetry feed rows visible at once. Defaults to the
   *  `telemetryMaxRows` token (8). */
  telemetryMaxRows?: number;
  /** Extra slot for downstream widgets (narrative beats in O5, etc). */
  children?: ReactNode;
}

const containerStyleBase: CSSProperties = {
  position: "absolute",
  inset: 0,
  pointerEvents: "none",
};

/**
 * Subtle 1px graph grid layered behind every panel — channels the
 * retro-scifi reference. Two crossed linear-gradients keep the cost
 * negligible vs an SVG pattern. The grid is purely decorative and
 * does NOT cover the chromakey / transparent backgrounds (chromakey
 * needs a flat color for OBS keying; transparent is meant to composite
 * over arbitrary streamer scenes).
 */
function gridBackground(): CSSProperties {
  return {
    backgroundImage:
      "linear-gradient(to right, var(--pp-grid-color) 0 1px, transparent 1px), linear-gradient(to bottom, var(--pp-grid-color) 0 1px, transparent 1px)",
    backgroundSize: "var(--pp-grid-size) var(--pp-grid-size)",
  };
}

function bgFor(mode: MatchOverlayProps["background"]): CSSProperties {
  switch (mode) {
    case "chromakey":
      return { background: "#00ff00" };
    case "transparent":
      return { background: "transparent" };
    case "normal":
    default:
      return gridBackground();
  }
}

export function MatchOverlay({
  battle,
  tick,
  panels = DEFAULT_PANELS,
  tokens,
  score,
  ratingA,
  ratingB,
  archetypeA,
  archetypeB,
  showZoneLegend,
  background = "normal",
  cosmeticA,
  cosmeticB,
  loadoutA,
  loadoutB,
  transcriptA,
  transcriptB,
  showReasoningStats,
  telemetryMaxRows,
  children,
}: MatchOverlayProps) {
  const resolvedTokens = resolveTokens(tokens);
  const frame = frameAt(battle, tick);
  const mechA: MechState = frame.state.mech_a;
  const mechB: MechState = frame.state.mech_b;
  const showMetadata = panels.includes("metadata");
  const showStatA = panels.includes("statA");
  const showStatB = panels.includes("statB");
  const showReasoningA = panels.includes("reasoningA");
  const showReasoningB = panels.includes("reasoningB");
  const showTelemetry = panels.includes("telemetry");
  const showAccolades = panels.includes("accolades");
  const stripHeight = parseStripHeight(resolvedTokens.stripHeight);
  // Stack gap below the metadata strip — unified for side panels and
  // accolade flash so they share a common visual rhythm. Audit fix:
  // accolade previously used 16 here, sides used 12 — no reason for the
  // mismatch.
  const stackGap = parsePxToken(resolvedTokens.stackGap, 12);
  const sideTopOffset = (showMetadata ? stripHeight : 0) + stackGap;
  // O3 + O5 share the event stream — pull once. Memoization upstream
  // makes the call cheap; the slicing is per-tick and bounded.
  const stream = showTelemetry || showAccolades ? eventStream(battle) : null;
  const telemetry =
    showTelemetry && stream
      ? telemetryRows(stream, frame.tick, telemetryMaxRows ?? resolvedTokens.telemetryMaxRows)
      : [];
  // Accolades derive deterministically from the full event stream, so
  // scrubbing tick re-renders the correct visible one without state.
  const allAccolades = showAccolades && stream ? detectAccolades(stream) : [];
  const activeAccolade =
    showAccolades && allAccolades.length > 0
      ? visibleAccoladeAt(allAccolades, frame.tick, resolvedTokens.accoladeWindowTicks)
      : null;
  // Position the accolade just under the metadata strip (or top edge
  // when metadata is hidden) so it doesn't crash into side panels.
  const accoladeTopOffset = (showMetadata ? stripHeight : 0) + stackGap;

  return (
    <div
      style={{
        ...containerStyleBase,
        ...tokensToCssVars(resolvedTokens),
        ...bgFor(background),
      }}
      data-pp-overlay
    >
      {showMetadata ? (
        <MatchMetadataStrip
          battle={battle}
          tick={frame.tick}
          score={score}
          ratingA={ratingA}
          ratingB={ratingB}
          archetypeA={archetypeA}
          archetypeB={archetypeB}
        />
      ) : null}
      {showStatA ? (
        <SideStatPanel
          mech={mechA}
          side="A"
          name={battle.openMeta?.agent_a?.name ?? "Agent A"}
          archetype={archetypeA}
          showZoneLegend={showZoneLegend}
          topOffset={sideTopOffset}
          cosmeticSlot={cosmeticA}
          loadoutSlot={loadoutA}
        />
      ) : null}
      {showStatB ? (
        <SideStatPanel
          mech={mechB}
          side="B"
          name={battle.openMeta?.agent_b?.name ?? "Agent B"}
          archetype={archetypeB}
          showZoneLegend={showZoneLegend}
          topOffset={sideTopOffset}
          cosmeticSlot={cosmeticB}
          loadoutSlot={loadoutB}
        />
      ) : null}
      {showReasoningA ? (
        <ReasoningPanel
          transcript={transcriptA ?? []}
          turn={frame.tick}
          side="A"
          name={battle.openMeta?.agent_a?.name ?? "Agent A"}
          showStats={showReasoningStats}
        />
      ) : null}
      {showReasoningB ? (
        <ReasoningPanel
          transcript={transcriptB ?? []}
          turn={frame.tick}
          side="B"
          name={battle.openMeta?.agent_b?.name ?? "Agent B"}
          showStats={showReasoningStats}
        />
      ) : null}
      {showTelemetry ? <TelemetryFeed rows={telemetry} /> : null}
      {showAccolades ? (
        <AccoladeFlash accolade={activeAccolade} topOffset={accoladeTopOffset} />
      ) : null}
      {children}
    </div>
  );
}

/** Parse a CSS length like "44px" to a number; falls back to default. */
function parseStripHeight(raw: string): number {
  const n = parseFloat(raw);
  if (Number.isFinite(n) && n > 0) return n;
  return parseFloat(DEFAULT_TOKENS.stripHeight) || 44;
}

/** Parse a CSS length token to a number with a fallback. */
function parsePxToken(raw: string, fallback: number): number {
  const n = parseFloat(raw);
  return Number.isFinite(n) && n >= 0 ? n : fallback;
}
